/**
 * CDN URL Helper
 * Originals > 10 MB → served from ImageKit (uncompressed)
 * Everything else (thumbnails, modal, small originals) → Cloudinary
 */

const CLOUD = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
const CDN_BASE = `https://res.cloudinary.com/${CLOUD}/image/upload`;
const IK_BASE = import.meta.env.VITE_IMAGEKIT_URL_ENDPOINT;

// Originals > 10 MB — served uncompressed from ImageKit
const IMAGEKIT_ONLY = new Set([
  'Anime/One-Piece/Luffy_Gear_5',
  'Aesthetic/The_Long_Walk_Home',
  'Anime/Horimiya/Hori_and_Miya_The_Urban_Drip',
  'Fantasy/Pink_sakura_tree_anime_landscape',
  'Fantasy/Ocean_clouds_sunset_dreamy_landscape',
  'Anime/Hatsune-Miku/Hatsune_Miku_in_Tokyo_vibes',
  'Aesthetic/Cosmic_Lullaby_Over_Concrete_Jungles',
  'Fantasy/Anime_girl_with_white_hair_and_headphones',
  'Fantasy/Twilight_cityscape_with_cherry_blossoms',
  'Nature/The_Gilded_Sentinel',
  'Anime/Curious_kittens_on_a_mossy_roof',
  'Anime/One-Piece/The_Will_of_D_and_the_Azure_Horizon',
  'Fantasy/Twilight_serenity_in_the_pine_forest',
  'Aesthetic/The_Silent_Symphony_of_the_City',
  'Anime/Weathering-With-You/Weathering_with_you_-_The_Girl_Who_Changed_the_World',
  'Aesthetic/Sunflower_reflections_in_her_glass',
  'Nature/Sunset_over_the_riverside_cityscapes',
  'Anime/Weathering-With-You/Weathering_With_You_-_The_Great_Escape',
  'Anime/One-Piece/Luffys_Graffiti_Playground',
  'Fantasy/Glow_of_the_Lantern_Province',
  'Nature/A_peaceful_rural_landscape_under_blue_skies',
  'Fantasy/A_peaceful_night_by_the_lake',
  'Nature/The_Astral_Orchard',
  'Anime/Attack-on-titan/Chibi_Mikasas_Resolve',
  'Aesthetic/A_Flutter_in_the_Stillness',
  'Nature/Whispers_of_the_Spring_Moon',
  'Anime/Spy-x-Family/Anya_Forger_Digital_Moodboard',
  'Anime/One-Piece/The_Fire_Fists_Horizon',
  'Anime/Weathering-With-You/Weathering_With_You',
  'Aesthetic/Stardust__Shiba__and_Sentiments',
  'Anime/High-School-DxD/High_School_DxD',
  'Fantasy/Silhouettes_in_Serenity',
  'Nature/A_Crescent_Dream',
  'Anime/Toradora/Toradora',

  'Gaming/Genshin-Impact/lumines-golden-hour-rest',
  'Anime/My-Teen-Romantic-Comedy-SNAFU---Oregairu/yui-and-hikigaya-afternoon-in-the-clubroom',]);

function sanitizeFolderName(name) {
  return name.replace(/\s+/g, '-');
}

function sanitizePublicId(name) {
  return name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9._-]/g, '_');
}

// NSFW wallpapers recategorised to Anime in data.js but still stored under Ecchi/ in CDN
const CDN_FOLDER_OVERRIDE = new Map([
  ['Anime/Lavender_Hair_Slip_Dress_Morning', 'Ecchi/Lavender_Hair_Slip_Dress_Morning'],
  ['Anime/Pink_Hair_Morning_Coffee',         'Ecchi/Pink_Hair_Morning_Coffee'],
  ['Anime/Silver_Hair_Yukata_Entrance',      'Ecchi/Silver_Hair_Yukata_Entrance'],
  ['Anime/Beach/Brown_Hair_Pool_Side_Bikini','Ecchi/Beach/Brown_Hair_Pool_Side_Bikini'],
  ['Anime/Quintessential-Quintuplets/The_Bold_Sister_Nino', 'Ecchi/Quintessential-Quintuplets/The_Bold_Sister_Nino'],
  ['Anime/Roshidere/Alya_s_Golden_Hour_Smile', 'Ecchi/Roshidere/Alya_s_Golden_Hour_Smile'],
  ['Anime/Rent-a-Girlfriend/Rental_Girlfriend_s_Summer_Retreat', 'Ecchi/Rent-a-Girlfriend/Rental_Girlfriend_s_Summer_Retreat'],
  ['Anime/A-Couple-of-Cuckoos/Yanami_s_Quiet_Shoreline', 'Ecchi/A-Couple-of-Cuckoos/Yanami_s_Quiet_Shoreline'],
  ['Anime/Quintessential-Quintuplets/Yotsuba_s_Summer_Energy', 'Ecchi/Quintessential-Quintuplets/Yotsuba_s_Summer_Energy'],
  ['Anime/My-Dress-Up-Darling/Marin_s_Late_Night_Workshop', 'Ecchi/My-Dress-Up-Darling/Marin_s_Late_Night_Workshop'],
  ['Anime/Original/Sunlit_Bedroom_Duo', 'Anime/Crossover/Chizuru_and_Alya_Morning_Glow'],
  ['Anime/Original/Tropical_Water_Fight', 'Anime/Quintessential-Quintuplets/Yotsuba_Beach_Water_Fight'],
  ['Anime/Original/Game_Room_Duo_Tease', 'Anime/Crossover/Nino_and_Chizuru_Game_Room_Tease'],
  ['Anime/Rent-a-Girlfriend/Chizuru_Mizuhara_1786697279772', 'Ecchi/Rent-a-Girlfriend/Chizuru_Mizuhara_1786697279772'],
  ['Anime/Rent-a-Girlfriend/mami_nanami__steam____rent_a_girlfriend__ai_art__by_cosplaymakin_dmmbzqs-pre', 'Ecchi/Rent-a-Girlfriend/mami_nanami__steam____rent_a_girlfriend__ai_art__by_cosplaymakin_dmmbzqs-pre'],
  ['Anime/Konosuba/aqua__konosuba__ai_art__by_cosplaymakin_dmclqag-414w-2x', 'Ecchi/Konosuba/aqua__konosuba__ai_art__by_cosplaymakin_dmclqag-414w-2x'],
]);

function getPublicId(category, filename) {
  const sanitizedCategory = sanitizeFolderName(category);
  const parts = filename.split('/');
  const sanitizedPath = parts
    .map((part, i) =>
      i === parts.length - 1 ? sanitizePublicId(part) : sanitizeFolderName(part)
    )
    .join('/');
  const publicId = `${sanitizedCategory}/${sanitizedPath}`;
  // Reroute recategorised wallpapers to their actual CDN storage folder
  for (const [prefix, replacement] of CDN_FOLDER_OVERRIDE) {
    if (publicId.startsWith(prefix)) {
      return replacement + publicId.slice(prefix.length);
    }
  }
  return publicId;
}

function buildCloudinaryUrl(publicId, transformations = []) {
  const tr = transformations.length > 0 ? transformations.join(',') + '/' : '';
  return `${CDN_BASE}/${tr}${publicId}`;
}

function buildImageKitUrl(category, filename, transformations = []) {
  const sanitizedCategory = sanitizeFolderName(category);
  const parts = filename.split('/');
  const sanitizedPath = parts
    .map((part, i) =>
      i === parts.length - 1
        ? part.replace(/[^a-zA-Z0-9._-]/g, '_')
        : sanitizeFolderName(part)
    )
    .join('/');
  const base = `${IK_BASE}/${sanitizedCategory}/${sanitizedPath}`;
  if (transformations.length === 0) return base;
  return base.replace(/\/([^/]+)$/, `/tr:${transformations.join(',')}/$1`);
}

export function getImageKitUrl(category, filename, options = {}) {
  const publicId = getPublicId(category, filename);
  const tr = [];
  if (options.width)   tr.push(`w_${options.width}`);
  if (options.height)  tr.push(`h_${options.height}`);
  if (options.quality) tr.push(`q_${options.quality}`);
  if (options.format)  tr.push(`f_${options.format}`);
  if (options.auto)    tr.push('f_auto', 'q_auto');

  if (IMAGEKIT_ONLY.has(publicId)) {
    const ikTr = [];
    if (options.width)   ikTr.push(`w-${options.width}`);
    if (options.height)  ikTr.push(`h-${options.height}`);
    if (options.quality) ikTr.push(`q-${options.quality}`);
    if (options.format)  ikTr.push(`f-${options.format}`);
    if (options.auto)    ikTr.push('f-auto', 'q-auto');
    return buildImageKitUrl(category, filename, ikTr);
  }

  return buildCloudinaryUrl(publicId, tr);
}

export function getThumbnailUrl(category, filename, isVertical = true) {
  const publicId = getPublicId(category, filename);
  const w = isVertical ? 400 : 600;
  if (IMAGEKIT_ONLY.has(publicId)) {
    // Large originals live on ImageKit — apply transformation directly on the original
    return buildImageKitUrl(category, filename, [`w-${w}`, 'q-70', 'f-webp']);
  }
  return buildCloudinaryUrl(publicId, [`c_limit,w_${w}`, 'q_70', 'f_webp']);
}

export function get1080pUrl(category, filename) {
  const publicId = getPublicId(category, filename);
  if (IMAGEKIT_ONLY.has(publicId)) {
    // Large originals live on ImageKit — apply transformation directly on the original
    return buildImageKitUrl(category, filename, ['w-1920', 'h-1920', 'c-at_max']);
  }
  return buildCloudinaryUrl(publicId, ['c_limit,w_1920,h_1920,f_png']);
}

export function getOriginalUrl(category, filename) {
  const publicId = getPublicId(category, filename);
  if (IMAGEKIT_ONLY.has(publicId)) {
    // orig-true bypasses ImageKit's auto-format conversion (otherwise serves WebP to modern browsers)
    return buildImageKitUrl(category, filename, ['orig-true']);
  }
  // No format transformation — serve Cloudinary's native stored format (JPG)
  return buildCloudinaryUrl(publicId, []);
}

export function getModalUrl(category, filename) {
  return get1080pUrl(category, filename);
}

// Triggers a file download. Uses Fetch+Blob for Cloudinary (avoids CDN transformation errors)
// and window.open with ik-attachment for ImageKit (already works natively).
export async function triggerDownload(cdnUrl, displayFilename) {
  if (cdnUrl.includes('res.cloudinary.com')) {
    try {
      const resp = await fetch(cdnUrl, { mode: 'cors' });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = Object.assign(document.createElement('a'), { href: blobUrl, download: displayFilename });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      window.open(cdnUrl, '_blank');
    }
  } else {
    window.open(cdnUrl + (cdnUrl.includes('?') ? '&' : '?') + 'ik-attachment=true', '_blank');
  }
}

export default {
  getImageKitUrl,
  getThumbnailUrl,
  get1080pUrl,
  getOriginalUrl,
  getModalUrl,
};
